import React, { useCallback, useEffect, useMemo } from 'react';

import debounce from 'lodash/debounce';

import AssigneeFilter from './assignee';
import DateRangeFilter from './date_range';
import DateTypeFilter from './date_type';
import DealUserFilter from './deal_user';
import OfficeFilter from './office';
import ParentTypeFilter from './parent_type';
import QueryFilter from './query';
import StatusFilter from './status';
import { getDefaults, isFilterEnabled, serializeFilters } from './helpers';
import { useQueryParamsState } from 'hooks/use_query_param_state';

import type { ApiFilters, Filters, FilterProps } from './types';
import { Col, Row } from 'react-bootstrap-5';

const config = {
  assigneeId: 'number',
  completed: 'boolean',
  dealUserId: 'number',
  fromDate: 'string',
  officeId: 'number',
  open: 'boolean',
  overdue: 'boolean',
  parentType: 'string',
  query: 'string',
  tbd: 'boolean',
  toDate: 'string',
  allTasks: 'boolean',
} as const;

export interface Props {
  assignee?: FilterProps;
  children?: React.ReactNode;
  dateRange?: FilterProps;
  dateType?: FilterProps;
  dealUser?: FilterProps;
  features?: {
    marketingListings?: boolean;
    taskAttachmentExceptions?: boolean;
  };
  oneLine?: boolean;
  userId?: number;
  options: {
    offices: {
      label: string;
      value: number;
    }[];
    users: {
      id: number;
      name: string;
    }[];
  };
  parent?: FilterProps;
  status?: FilterProps;
  onChange: (filters: ApiFilters) => void;
  onChangePending: () => void;
}

export default function TasksIndexFilters({
  children,
  features,
  oneLine,
  options,
  onChange,
  onChangePending,
  userId,
  ...props
}: Props) {
  const users = useMemo(() => options.users.filter(option => option.id), []);
  const [filters, setFilters] = useQueryParamsState<Filters>(
    config,
    getDefaults({
      dateRangeEnabled: isFilterEnabled(props, 'dateRange'),
      tbdEnabled: isFilterEnabled(props, 'dateType'),
      assigneeId: (isFilterEnabled(props, 'assignee') && options.users.find((user => user.id === userId))) ? userId : null,
    }),
    { prefix: 'tasks' }
  );

  // Initialize parent component once from initial query param state
  useEffect(() => {
    onChange(serializeFilters(filters));
  }, []);

  const debouncedOnChange = useCallback(debounce(onChange, 500), [onChange]);
  function changeFilters(changes: Partial<Filters>, debounce = false) {
    const changedFilters = { ...filters, ...changes } as Filters;
    setFilters(changedFilters);
    onChangePending();
    const callback = debounce ? debouncedOnChange : onChange;
    callback(serializeFilters(changedFilters));
  }

  const filterProps = { ...filters, ...features, onChange: changeFilters };

  const filterComponents = <>
    {isFilterEnabled(props, 'status') && <StatusFilter {...filterProps} />}
    {isFilterEnabled(props, 'parent') && <ParentTypeFilter {...filterProps} />}
    {isFilterEnabled(props, 'dateType') && <DateTypeFilter {...filterProps} />}
    {isFilterEnabled(props, 'office') && options.offices.length > 0 && <OfficeFilter {...filterProps} offices={options.offices} />}
    {isFilterEnabled(props, 'dealUser') && <DealUserFilter {...filterProps} users={users} />}
    {isFilterEnabled(props, 'assignee') && <AssigneeFilter {...filterProps} users={users} />}
  </>;

  return (
    <div className="search-page">
      <Row>
        <Col>
          {oneLine && (
            <div className="d-flex flex-wrap align-items-center gap-2">
              <div style={{ minWidth: '300px' }}>
                <QueryFilter {...filterProps} onChange={changes => changeFilters(changes, true)} />
              </div>
              {filterComponents}
            </div>
          )}
          {!oneLine && (
            <div className="d-flex flex-column gap-2">
              <QueryFilter {...filterProps} onChange={changes => changeFilters(changes, true)} />
              <div className="d-flex flex-wrap align-items-center gap-2">
                {filterComponents}
              </div>
            </div>
          )}
        </Col>
        {isFilterEnabled(props, 'dateRange') &&
          <Col xs="auto">
             <DateRangeFilter {...filterProps} />
          </Col>
        }
        {children &&
          <Col xs="auto">
             {children}
          </Col>
        }
      </Row>
    </div>
  );
}
